'use strict';

const _ = require('lodash');
const moment = require('moment');
// Vue is a frontend package so must be included like this for compatibility with the frontend webpack in elsa
// otherwise simply doing `require('vue');` won't work in elsa
const Vue = require('vue/dist/vue');

const utils = require('../utils');
const { DAY_FORMAT, WEEK_TIMEFRAME } = require('../consts');

function create(reactiveCashflow) {
  return new Vue({
    data: {
      budgetDate: reactiveCashflow.budgetDate,
      lastUpdatedAt: reactiveCashflow.lastUpdatedAt,
      totalVariables: reactiveCashflow.variableBalancedAmount,
      transactions: reactiveCashflow.variableTransactions,
    },
    computed: {
      startOfFirstWeek() {
        return moment(this.budgetDate).startOf('week');
      },
      plannedTransactions() {
        return _.filter(this.transactions, t => t.planned);
      },
      plannedSum() {
        return utils.sumTransactions(this.plannedTransactions);
      },
      variableTransactions() {
        return _.reject(this.transactions, t => t.planned);
      },
      transactionsByWeek() {
        return _.groupBy(this.variableTransactions, ({ transactionDate }) => moment(transactionDate).diff(this.startOfFirstWeek, 'weeks'));
      },
      balancedWeeksTransactionSum() {
        return _.chain(this.weeksMeta)
          .filter(week => week.timeframe !== WEEK_TIMEFRAME.CURRENT)
          .flatMap(week => this.transactionsByWeek[week.index])
          .compact()
          .thru(transactions => utils.sumTransactions(transactions))
          .value() || 0;
      },
      dailyVariables() {
        const variablesRemaining = (this.totalVariables - this.balancedWeeksTransactionSum - this.plannedSum);
        // avoid zero division if no days remaining
        return this.remainingDays > 0 ? (variablesRemaining / this.remainingDays) : 0;
      },
      remainingDays() {
        return _.chain(this.weeksMeta)
          .reject(week => week.timeframe === WEEK_TIMEFRAME.PAST)
          .sumBy(week => week.days)
          .value();
      },
      weeksMeta() {
        return utils.getWeeks(this.budgetDate, this.lastUpdatedAt);
      },
      potentialWeeks() {
        return _.map(this.weeksMeta, weekMeta => {
          const sum = utils.sumTransactions(this.transactionsByWeek[weekMeta.index]);
          return _.extend({
            available: weekMeta.timeframe === WEEK_TIMEFRAME.PAST ? sum : (this.dailyVariables * weekMeta.days),
            sum,
            transactions: this.transactionsByWeek[weekMeta.index],
          }, weekMeta);
        });
      },
      weeks() {
        const futureWeekDays = _.chain(this.weeksMeta).filter(w => w.timeframe === WEEK_TIMEFRAME.FUTURE).sumBy(w => w.days).value();
        return _.map(this.potentialWeeks, week => {
          let remaining;
          if (week.timeframe === WEEK_TIMEFRAME.PAST) {
            remaining = 0;
          } else if (week.timeframe === WEEK_TIMEFRAME.CURRENT) {
            remaining = _.max([week.available - week.sum, 0]);
          } else {
            const deficitToBalance = (_getCurrentMonthDeficit(this.potentialWeeks) / futureWeekDays) * week.days;
            remaining = _.max([(week.available - week.sum) + deficitToBalance, 0]);
          }
          return _.extend({ remaining }, week);
        });
      },
    },
    methods: {
      toJson() { // DEPRECATED
        const weeks = _.map(this.weeks, week => {
          return {
            weekIndex: week.index,
            days: week.days,
            startDate: week.startDate.format(DAY_FORMAT),
            endDate: week.endDate.format(DAY_FORMAT),
            sum: week.sum,
            remaining: week.remaining,
            available: week.available,
            transactions: _.cloneDeep(week.transactions) || [],
          };
        });
        return {
          budgetDate: this.budgetDate,
          lastUpdatedAt: this.lastUpdatedAt,
          totalVariables: this.totalVariables,
          available: this.totalVariables,
          sum: utils.sumTransactions(this.transactions),
          plannedTransactions: _.cloneDeep(this.plannedTransactions),
          weeks,
        };
      },
      getCashculations() {
        const weeks = _.map(this.weeks, week => {
          return {
            weekIndex: week.index,
            days: week.days,
            startDate: week.startDate.format(DAY_FORMAT),
            endDate: week.endDate.format(DAY_FORMAT),
            sum: week.sum,
            remaining: week.remaining,
            available: week.available,
          };
        });
        return {
          budgetDate: this.budgetDate,
          lastUpdatedAt: this.lastUpdatedAt,
          totalVariables: this.totalVariables,
          available: this.totalVariables,
          sum: utils.sumTransactions(this.transactions),
          weeks,
        };
      },
    },
  });
}

function _getCurrentMonthDeficit(weeks) {
  const currentWeek = _.find(weeks, w => w.timeframe === WEEK_TIMEFRAME.CURRENT);
  if (!currentWeek) {
    return 0;
  }
  return _.min([currentWeek.available - currentWeek.sum, 0]);
}

module.exports = {
  create,
};
